import React, { useEffect, useRef, useState } from "react";
import { File, Loader2, UploadCloud, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ALLOWED_TYPES_LABEL,
  COMPANY_DOCUMENT_CATEGORIES,
  DEFAULT_AUDIENCE,
  FILE_INPUT_ACCEPT,
  MAX_FILE_SIZE_MB,
  formatFileSize,
  isSameAudience,
  normalizeAudience,
  validateSelectedFile,
} from "./companyDocumentUtils";
import CompanyDocumentAudienceField from "./CompanyDocumentAudienceField";
import { fetchCompanyDocumentAudienceOptions } from "@/services/companyDocumentService";
import { getErrorMessage } from "@/utils/errorHandler";

const TITLE_MAX = 150;
const DESCRIPTION_MAX = 1000;

const COPY = {
  create: { title: "Upload Company Document", submit: "Upload", busy: "Uploading..." },
  edit: { title: "Edit Document Details", submit: "Save Changes", busy: "Saving..." },
  replace: { title: "Replace File", submit: "Replace File", busy: "Uploading..." },
};

/**
 * mode "create":  title, category, description, file
 * mode "edit":    title, category, description
 * mode "replace": file only (keeps the document's details)
 *
 * onSubmit receives { title, category, description, file, audience, audienceChanged,
 * acknowledgementRequired } and should throw on failure so the dialog stays open
 * with the entered values.
 */
export default function CompanyDocumentFormDialog({ open, mode = "create", document, onOpenChange, onSubmit }) {
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({ title: "", category: "other", description: "" });
  const [audience, setAudience] = useState(DEFAULT_AUDIENCE);
  const [acknowledgementRequired, setAcknowledgementRequired] = useState(false);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [audienceOptions, setAudienceOptions] = useState(null);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsError, setOptionsError] = useState(null);

  const showDetails = mode !== "replace";
  const showFile = mode !== "edit";
  const copy = COPY[mode];

  useEffect(() => {
    if (!open) return;
    setForm({
      title: document?.title || "",
      category: document?.category || "other",
      description: document?.description || "",
    });
    setAudience(normalizeAudience(mode === "create" ? DEFAULT_AUDIENCE : document?.audience));
    setAcknowledgementRequired(mode !== "create" && Boolean(document?.acknowledgement_required));
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [open, document, mode]);

  const loadAudienceOptions = async () => {
    setOptionsLoading(true);
    setOptionsError(null);
    try {
      setAudienceOptions(await fetchCompanyDocumentAudienceOptions());
    } catch (error) {
      setOptionsError(getErrorMessage(error, "Couldn't load departments and employees"));
    } finally {
      setOptionsLoading(false);
    }
  };

  // Fresh options each time the dialog opens for details, so new employees and
  // departments show up without a page reload.
  useEffect(() => {
    if (open && showDetails) loadAudienceOptions();
  }, [open, showDetails]);

  const audienceChanged = mode === "create" || !isSameAudience(audience, document?.audience);
  const audienceComplete =
    audience.mode === "all" ||
    (audience.mode === "departments" && audience.departments.length > 0) ||
    (audience.mode === "selected" && audience.employee_ids.length > 0);

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    const error = validateSelectedFile(selected);
    if (error) {
      toast.error(error);
      e.target.value = "";
      return;
    }
    setFile(selected);
    // Save typing on a phone: an empty title starts as the file name.
    if (mode === "create" && !form.title.trim()) {
      const base = selected.name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ").trim();
      setForm((f) => ({ ...f, title: base.slice(0, TITLE_MAX) }));
    }
  };

  const canSubmit =
    !saving &&
    (!showDetails || (form.title.trim().length > 0 && audienceComplete)) &&
    (!showFile || Boolean(file));

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      await onSubmit({
        title: form.title.trim(),
        category: form.category,
        description: form.description.trim(),
        file,
        audience,
        audienceChanged,
        acknowledgementRequired,
      });
      onOpenChange(false);
    } catch {
      // The caller reports the error; keep the dialog open with the input.
    } finally {
      setSaving(false);
    }
  };

  const fileSection = showFile && (
    <div className="space-y-1.5">
      <Label>File *</Label>
      <div
        role="button"
        tabIndex={0}
        className="cursor-pointer rounded-lg border-2 border-dashed p-4 text-center transition-all hover:bg-gray-50 dark:hover:bg-gray-900/30"
        onClick={() => fileInputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            fileInputRef.current?.click();
          }
        }}
      >
        {!file ? (
          <div className="flex flex-col items-center text-gray-500">
            <UploadCloud className="mb-2 h-7 w-7 text-gray-400" />
            <p className="font-medium">Tap to choose a file</p>
            <p className="mt-1 text-xs text-gray-500">
              {ALLOWED_TYPES_LABEL} • Max {MAX_FILE_SIZE_MB}MB
            </p>
          </div>
        ) : (
          <div className="grid w-full grid-cols-[1fr_auto] items-center gap-2 overflow-hidden rounded bg-primary/10 p-2">
            <div className="flex min-w-0 items-center">
              <File className="mr-2 h-4 w-4 flex-shrink-0" />
              <span className="truncate text-left text-sm" title={file.name}>
                {file.name}
              </span>
              <span className="ml-2 whitespace-nowrap text-xs text-muted-foreground">
                {formatFileSize(file.size)}
              </span>
            </div>
            <button
              type="button"
              aria-label="Remove selected file"
              className="p-1"
              onClick={(e) => {
                e.stopPropagation();
                setFile(null);
                if (fileInputRef.current) fileInputRef.current.value = "";
              }}
            >
              <X className="h-5 w-5 text-red-500 hover:text-red-600" />
            </button>
          </div>
        )}
      </div>
      <input ref={fileInputRef} type="file" accept={FILE_INPUT_ACCEPT} onChange={handleFileChange} hidden />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(next) => !saving && onOpenChange(next)}>
      {/* Header and actions stay put; only the form scrolls — so Upload/Cancel
          are always reachable on a phone. */}
      <DialogContent className="flex flex-col gap-0 overflow-hidden p-0 sm:max-w-[520px]">
        <DialogHeader className="shrink-0 px-5 pb-3 pt-5 pr-12 text-left sm:px-6 sm:pt-6">
          <DialogTitle>{copy.title}</DialogTitle>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 pb-5 pt-1 sm:px-6">
          {mode === "replace" && document && (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Upload a new file for <span className="font-medium">"{document.title}"</span>. The
              title, category, description and visibility stay the same.
              {document.acknowledgement_required &&
                " Employees will be asked to acknowledge the new file, even if they acknowledged the old one."}
            </p>
          )}

          {fileSection}

          {showDetails && (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="company-doc-title">Document Title *</Label>
                <Input
                  id="company-doc-title"
                  value={form.title}
                  maxLength={TITLE_MAX}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g., Leave Policy 2026"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="company-doc-category">Category</Label>
                <Select value={form.category} onValueChange={(value) => setForm({ ...form, category: value })}>
                  <SelectTrigger id="company-doc-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COMPANY_DOCUMENT_CATEGORIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="company-doc-description">Description</Label>
                <Textarea
                  id="company-doc-description"
                  value={form.description}
                  maxLength={DESCRIPTION_MAX}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="What is this document about? (optional)"
                  rows={2}
                />
              </div>

              <CompanyDocumentAudienceField
                value={audience}
                onChange={setAudience}
                options={audienceOptions}
                loading={optionsLoading}
                error={optionsError}
                onRetry={loadAudienceOptions}
              />

              <div className="flex items-start justify-between gap-3 rounded-lg border border-border p-3">
                <div className="min-w-0">
                  <Label htmlFor="company-doc-acknowledgement">Require acknowledgement</Label>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Employees who can see this document are notified and must confirm they have read
                    it. You can see who has.
                  </p>
                </div>
                <Switch
                  id="company-doc-acknowledgement"
                  checked={acknowledgementRequired}
                  onCheckedChange={setAcknowledgementRequired}
                />
              </div>
            </>
          )}

        </div>

        <DialogFooter className="shrink-0 flex-row gap-2 space-x-0 border-t border-border px-5 py-3 sm:space-x-0 sm:px-6">
          <Button
            variant="outline"
            className="flex-1 sm:flex-none"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button className="flex-1 sm:flex-none" onClick={handleSubmit} disabled={!canSubmit}>
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {copy.busy}
              </>
            ) : (
              copy.submit
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
